import { useLocation } from "react-router-dom";
import { useNetwork } from "../../../context/NetworkContext";
import { usePermission } from "../../../context/PermissionContext";
import { useEffect, useRef, useState } from "react";
import { useShowAlert } from "../../../context/AlertContext";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import DateConverter from "@heartinz/date_and_time_processor";
import AxiosInstance from "../../../utils/AxiosInstance";
import handleError from "../../../constants/handleError";
import {
  addSafe,
  getReturnAmountList,
  getReturnGrandTotal,
} from "@heartinz/finance";
import { CircularProgress } from "@mui/material";
import PreviousReturnModal from "./PreviousRetrunModal";
import { useAuth } from "../../../context/AuthProvider";
import InvoiceTempPreview from "../settings/Invoice/InvoiceTempPreview";
import AddRetrunProduct from "./AddProduct";
import PaymentMethod from "./PaymentModal";
import RemarkModal from "../sale/Remark";
import { useSaleContext } from "../../../context/SaleContext";
import TemplateModalLayout from "../../../templates/modules/TemplateModalLayout";
import { CheckIcon, ChevronRightIcon, CircleIcon, DeleteIcon, EditIcon, InfoCircleIcon, LineDoubleIcon, MinusIcon, ShowIcon, XIcon } from "../../../constants/Icons";

const ReturnSale = ({ onDone, isActive }) => {
  const { storeSettings } = usePermission();
  const [showProductModal, setShowProductModal] = useState(false);
  // const [showPreviousModal, setShowPreviousModal] = useState(false);
  // const [returnCart, setReturnCart] = useState([]);
  const { updateSettings } = useAuth();
  const { checkNetwork } = useNetwork();
  const showAlert = useShowAlert();
  const location = useLocation();

  // console.log("Sale ID from navigation state:", saleId);

  const RETURN_SALE_STORAGE_KEY = "return-sale-session";

  const [buttonLoading, setButtonLoading] = useState({ type: "", id: null });
  const [isViewModalOpen, setViewModalOpen] = useState(false);
  const [isViewReturnModalOpen, setViewReturnModalOpen] = useState(false);
  const [isViewData, setViewData] = useState(null);
  const [isViewReturnData, setViewReturnData] = useState(null);
  const [isViewTempModalOpen, setViewTempModalOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("");
  const [selectedReturnOption, setSelectedReturnOption] = useState("RETURN");
  const [selectTermalOption, setSelectTermalOption] = useState("");
  const templateId = storeSettings?.invoiceSettings?.template?.templateId;

  const [remark, setRemark] = useState("");
  const [remarkModalOpen, setRemarkModalOpen] = useState(false);

  const [proceedLoading, setProceedLoading] = useState(false);

  const [isRetrunProductModalOpen, setRetrunProductModalOpen] = useState(false);
  const [isRetrunProductData, setReturnProductData] = useState(null);

  const [editItem, setEditItem] = useState(null);
  const [editItemModalOpen, setEditItemModalOpen] = useState(false);

  const savedSession = (() => {
    try {
      return JSON.parse(localStorage.getItem(RETURN_SALE_STORAGE_KEY)) ?? {};
    } catch {
      return {};
    }
  })();

  const saleId = location.state?.saleId || savedSession.saleId;

  const [returnCart, setReturnCart] = useState([]);

  const [returnTotals, setReturnTotals] = useState({
    totalMRP: 0,
    totalCancelledPrice: 0,
    totalSoldPrice: 0,
    totalReturnAmount: 0,
    totalBasePrice: 0,
    totalTaxAmount: 0,
    totalQty: 0,
    grandTotal: 0,
  });
  const previousSaleId = useRef(saleId);
  // console.log(returnCart);

  // Assuming saleId is passed through navigation state

  const [paymentModal, setPaymentModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const { isSaleData, setSaleData } = useSaleContext();
  const [saleData, setSaleDataReturn] = useState(null);

  useEffect(() => {
    if (saleId) {
      fetchSale();
    }
  }, [saleId]);

  const fetchSale = async () => {
    try {
      const returnResponse = await AxiosInstance.get(
        `module/salereturn/available-balance/${saleId}`,
      );
      const returnData = returnResponse.data?.results;
      setSaleDataReturn(returnData);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSaleItems = async (saleid) => {
    try {
      if (!checkNetwork()) return;
      const response = await AxiosInstance.get(`module/sale/${saleid}`);
      return response.data.results;
    } catch (error) {
      handleError(error, showAlert);
      return null;
    }
  };

  // console.log("Sale Data:", saleData);

  const handleView = async (saleId) => {
    setButtonLoading({ type: "view", id: saleId });
    try {
      const data = await handleSaleItems(saleId);
      if (data) {
        setViewData(data);
        setViewModalOpen(true);
        setViewTempModalOpen(true);
      }
    } catch (error) {
      console.error("Error fetching view data:", error);
    } finally {
      setButtonLoading({ type: "", id: null });
    }
  };

  const handleSetTemplate = async (
    templateId,
    selectTermalOption,
    selectHalfA4Option,
    templateTheme,
    show = true,
  ) => {
    if (!checkNetwork()) return;
    try {
      const response = await AxiosInstance.patch(
        `module/storesettings/template`,
        {
          templateIdtemplateId: `${templateId}AND${selectTermalOption}AND${selectHalfA4Option}`,
          templateTheme,
        },
      );
      const results = response.data?.results?.invoiceSettings?.template;
      const id = results.templateId;
      const words = id.split("AND");
      setSelectedOption(words[0]);
      setSelectTermalOption(words[1]);
      setViewTempModalOpen(false);
      updateSettings({
        storeSettings: {
          invoiceSettings: {
            template: {
              templateId: results?.templateId,
              templateTheme: results?.templateTheme,
            },
            signature: results?.signature,
            termsAndConditions: results?.termsAndConditions,
          },
        },
      });
      {
        show && showAlert("success", "Template Selected...");
      }
    } catch (error) {
      handleError(error, showAlert);
    }
  };

  const calculateReturnTotal = (items = []) => {
    if (!items.length) {
      setReturnTotals({
        totalMRP: 0,
        totalCancelledPrice: 0,
        totalSoldPrice: 0,
        totalReturnAmount: 0,
        totalBasePrice: 0,
        totalTaxAmount: 0,
        totalQty: 0,
        grandTotal: 0,
      });
      return;
    }

    const totalmrp = items.reduce(
      (sum, item) => addSafe(sum, Number(item.lineTotalMrp || 0), 0),
      0,
    );

    const totalsaleprice = items.reduce(
      (sum, item) => addSafe(sum, Number(item.lineTotalSalePrice || 0), 0),
      0,
    );

    const totalsoldprice = items.reduce(
      (sum, item) => addSafe(sum, Number(item.lineTotalSoldPrice || 0), 0),
      0,
    );

    const totalReturnAmount = items.reduce(
      (sum, item) => addSafe(sum, Number(item.lineTotalReturnPrice || 0), 0),
      0,
    );

    const totalbaseprice = items.reduce(
      (sum, item) => addSafe(sum, Number(item?.lineTotalBasePrice || 0), 0),
      0,
    );

    const totaltaxamount = items.reduce(
      (sum, item) => addSafe(sum, Number(item?.lineTotalTaxAmount || 0), 0),
      0,
    );

    const totalCancelledAmount = items.reduce(
      (sum, item) =>
        addSafe(sum, Number(item?.lineTotalCancelledPrice || 0), 0),
      0,
    );

    const totalQty = items.reduce(
      (sum, item) => addSafe(sum, Number(item.quantity || 0), 0),
      0,
    );

    const grandTotal = getReturnGrandTotal(
      totalmrp,
      totalsaleprice,
      totalsoldprice,
      totalReturnAmount,
      totalCancelledAmount,
      totalbaseprice,
      totaltaxamount,
    );

    setReturnTotals({
      totalMRP: totalmrp,
      totalSoldPrice: grandTotal.totalsoldprice,
      totalCancelledPrice: grandTotal.totalCancelledAmount,
      totalsaleprice,
      totalReturnAmount: grandTotal.totalReturnAmount,
      totalBasePrice: totalbaseprice,
      totalTaxAmount: totaltaxamount,
      totalQty,
      grandTotal: grandTotal.returnGrandTotalRounded,
    });
  };

  const handleClose = () => {
    setViewModalOpen(false);
    setViewData(null);
  };

  const handleSoldPriceClose = () => {
    setRetrunProductModalOpen(false);
  };

  const handleRetrunProduct = (product) => {
    setReturnProductData(product);
    setRetrunProductModalOpen(true);
  };

  const handleEditRetrunItem = (item, index) => {
    setEditItem({
      ...item,
      cMode: "EDIT",
      cIndex: index,
    });

    setEditItemModalOpen(true);
  };
  const handleCloseEditModal = () => {
    setEditItemModalOpen(false);
  };

  const selectedSaleProducts =
    saleData?.saleBalanceItems?.length > 0
      ? saleData.saleBalanceItems
      : saleData?.saleitems || [];

  const handleDeleteReturnItem = (index) => {
    setReturnCart((prev) => {
      const updated = prev
        .filter((_, i) => i !== index)
        .map((item, i) => ({
          ...item,
          cIndex: i,
        }));

      return updated;
    });

    showAlert("success", "Item removed from return cart");
  };

  const handleAddToReturn = (product) => {
    // console.log("product", product);
    const isEditMode = product.cMode === "EDIT" || product.cIndex !== undefined;

    const saleItem = selectedSaleProducts.find(
      (item) => item.itemNo === product.itemNo,
    );

    const soldQty = Number(saleItem?.quantity || 0);

    let returnedQty = 0;

    if (isEditMode) {
      returnedQty = returnCart
        .filter((_, index) => index !== product.cIndex)
        .filter((item) => item.itemNo === product.itemNo)
        .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    } else {
      returnedQty = returnCart
        .filter((item) => item.itemNo === product.itemNo)
        .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    }

    const requestQty = Number(product.quantity || 0);

    if (returnedQty + requestQty > soldQty) {
      showAlert(
        "warning",
        `Only ${soldQty} quantity was sold. Remaining quantity: ${
          soldQty - returnedQty
        }`,
      );
      return;
    }

    // Passed validation -> update state
    setReturnCart((prev) => {
      if (isEditMode) {
        return prev.map((item, index) =>
          index === product.cIndex
            ? {
                ...product,
                cIndex: index,
              }
            : item,
        );
      }

      const existingIndex = prev.findIndex(
        (item) =>
          item.itemNo === product.itemNo &&
          item.returnPriceLock === product.returnPriceLock &&
          item.returnPrice === product.returnPrice &&
          item.lineTotalReturnPrice === product.lineTotalReturnPrice,
      );

      if (existingIndex === -1) {
        const amountList = getReturnAmountList(
          product.mrp,
          product.saleprice,
          product.soldprice,
          product.returnPrice,
          product.lineTotalReturnPrice,
          product.taxpercentage,
          Number(product.quantity),
          product.returnPriceLock,
        );

        return [
          ...prev,
          {
            ...product,
            ...amountList,
            cIndex: prev.length,
          },
        ];
      }

      const updated = [...prev];
      const existing = updated[existingIndex];

      const newQty =
        Number(existing.quantity || 0) + Number(product.quantity || 0);

      const amountList = getReturnAmountList(
        existing.mrp,
        existing.saleprice,
        existing.soldprice,
        existing.returnPrice,
        existing.lineTotalReturnPrice,
        existing.taxpercentage,
        newQty,
        existing.returnPriceLock,
      );

      updated[existingIndex] = {
        ...existing,
        quantity: newQty,

        discount: amountList.discount,

        returnPrice: amountList.returnPrice,
        cancelledPrice: amountList.cancelledPrice,

        lineTotalReturnPrice: amountList.lineTotalReturnPrice,
        lineTotalCancelledPrice: amountList.lineTotalCancelledPrice,

        lineTotalMrp: amountList.lineTotalMrp,
        lineTotalSalePrice: amountList.lineTotalSalePrice,
        lineTotalSoldPrice: amountList.lineTotalSoldPrice,
        lineGrandTotal: amountList.lineGrandTotal,

        lineTotalDiscount: amountList.lineTotalDiscount,
        lineTotalBasePrice: amountList.lineTotalBasePrice,
        lineTotalTaxAmount: amountList.lineTotalTaxAmount,

        lineTotalSgstAmount: amountList.lineTotalSgstAmount,
        lineTotalCgstAmount: amountList.lineTotalCgstAmount,
        lineTotalIgstAmount: amountList.lineTotalIgstAmount,
      };
      // console.log("amountList", amountList);
      return updated;
    });

    setShowProductModal(false);

    showAlert(
      "success",
      `${product.title} ${isEditMode ? "updated" : "added"} to return cart`,
    );
  };

  const handleRetrunClose = () => {
    setViewReturnModalOpen(false);
    setViewReturnData(null);
    onDone?.();
  };

  const handleCloseConfirmModal = () => {
    setShowConfirmModal(false);
  };

  const handlePaymentDone = async (payment) => {
    try {
      setProceedLoading(true);

      // 1. Create Return
      const createBody = {
        saleId,
        remarks: remark || "",
        returnItems: returnCart.map((item) => ({
          _id: item._id,
          itemNo: item.itemNo,
          quantity: Number(item.quantity),
          isSecondaryUnit: item.isSecondaryUnit ?? false,
          addToStock: item.addToStock ?? false,
          lineTotalReturnPrice: Number(item.lineTotalReturnPrice),
          itemRemarks: item.remark || "",
          itemCondition: item.conditions || "",
        })),
      };

      const createRes = await AxiosInstance.post(
        "module/salereturn/create",
        createBody,
      );

      const saleReturn = createRes.data.results;
      const returnId = saleReturn.saleReturnDoc._id;

      // 2. Payment
      await AxiosInstance.patch("module/salereturn/payment-add", {
        saleId: saleReturn.saleReturnDoc.saleId,
        saleReturnId: returnId,
        refundPaymentMethods: {
          CASH: CurrencyConverter.toSubunit(payment.cash || 0, "INR"),
          CARD: CurrencyConverter.toSubunit(payment.card || 0, "INR"),
          UPI: CurrencyConverter.toSubunit(payment.upi || 0, "INR"),
          PAYLATER: CurrencyConverter.toSubunit(payment.paylater || 0, "INR"),
          ONLINE: CurrencyConverter.toSubunit(payment.online || 0, "INR"),
          WAVEOFF: 0,
        },
      });
  
    
      console.log("isSaleData",isSaleData)
     
      // 3. Fetch updated return
      const previewRes = await AxiosInstance.get(
        `module/salereturn/${returnId}`,
      ); 
      const returnData = previewRes?.data?.results?.returnSaleData;

      setSaleData(prev =>
        prev.map(item => {
          if (item._id !== returnData.saleId) return item;
          return {
            ...item,
            saleTrack: "RETURNED"
          };
        })
      );
      setViewReturnData(previewRes.data.results);
      setViewReturnModalOpen(true);

      setPaymentModal(false);
      setReturnCart([]);
      localStorage.removeItem(RETURN_SALE_STORAGE_KEY);

      showAlert("success", "Return completed successfully");
    } catch (error) {
      handleError(error, showAlert);
    } finally {
      setProceedLoading(false);
    }
  };

  useEffect(() => {
    calculateReturnTotal(returnCart);
  }, [returnCart]);

  useEffect(() => {
    if (!saleId) return;

    localStorage.setItem(
      RETURN_SALE_STORAGE_KEY,
      JSON.stringify({
        saleId,

        returnCart,
      }),
    );
  }, [saleId, returnCart]);

  useEffect(() => {
    // First render
    if (!previousSaleId.current) {
      previousSaleId.current = saleId;
      return;
    }

    // Sale changed
    if (previousSaleId.current !== saleId) {
      setReturnCart([]);
      localStorage.removeItem(RETURN_SALE_STORAGE_KEY);

      previousSaleId.current = saleId;
    }
  }, [saleId]);

  useEffect(() => {
    const id = storeSettings?.invoiceSettings?.template?.templateId || "";
    const words = id ? id?.split("AND") : [];
    setSelectedOption(words[0]);
    setSelectTermalOption(words[1]);
  }, []);
 console.log("isViewModalOpen",isViewModalOpen ,"ok",isViewReturnModalOpen)
  return (
    <>
      <div>
        <div className="page-wrapper">
          <div className="page-content">
            <div className="row g-3">
              {/* Left Side - Selected Sale Products */}
              <div className="col-lg-6">
                <div className="card ">
                  <div className="card-header">
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">
                        {saleData?.salemode === "INVOICE"
                          ? "Tax Invoice"
                          : "Bill Receipt"}
                      </h5>

                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => {
                          handleView(saleId);
                        }}
                      >
                        <ShowIcon iClass={"me-1"}/>
                        View
                      </button>
                    </div>

                    <div className="mt-2 d-flex justify-content-between align-items-center">
                      <div>
                        <strong>
                          {saleData?.salemode === "INVOICE"
                            ? "Invoice No"
                            : "Bill No"}
                          :
                        </strong>{" "}
                        {saleData?.saleno || "-"}
                      </div>

                      <div>
                        <strong>Date:</strong>{" "}
                        {saleData?.saledate
                          ? DateConverter.ISOToDisplayFormat(saleData?.saledate)
                              ?.date
                          : "-"}
                      </div>
                    </div>
                  </div>

                  <div className="card-body  px-0">
                    <table className="table table-bordered mb-0">
                      <thead>
                        <tr>
                          <th className="text-center">S.No</th>
                          <th className="text-center">BarCode</th>
                          <th className="text-center">HSN</th>
                          <th>Product</th>
                          <th className="text-center">MRP</th>
                          <th className="text-center">Rate</th>
                          <th className="text-center">Qty</th>
                          <th className="text-center">Total</th>
                        </tr>
                      </thead>

                      <tbody>
                        {selectedSaleProducts.map((item, index) => (
                          <tr
                            key={item._id}
                            className={`position-relative cursor-pointer hover-row ${
                              Number(item.quantity) === 0
                                ? "bg-label-danger"
                                : ""
                            }`}
                            onClick={() => handleRetrunProduct(item)}
                          >
                            <td className="text-center">
                              {index + 1}
                              {item.imode === "SERVICE" && (
                                <span className="rounded fsz-10 ms-1">
                                  <CircleIcon iClass={"text-yellow"}/>
                                </span>
                              )}
                              {item.imode === "RECHARGE" && (
                                <span className="rounded fsz-10 ms-1">
                                  <CircleIcon iClass={"text-purple"}/>
                                </span>
                              )}
                              {item.imode === "PRODUCT" && (
                                <span className="rounded fsz-10 ms-1">
                                  <CircleIcon iClass={"text-primary"}/>
                                </span>
                              )}
                            </td>
                            <td className="text-center">{item.code || "-"}</td>
                            <td className="text-center">
                              {item.hsnsac || "-"}
                            </td>
                            <td>{item.title}</td>
                            <td className="text-center">
                              ₹
                              {CurrencyConverter.toCurrency(
                                item.mrp || 0,
                                "INR",
                              )}
                            </td>
                            <td className="text-center">
                              ₹
                              {CurrencyConverter.toCurrency(
                                item.saleprice || 0,
                                "INR",
                              )}
                            </td>
                            <td className="text-center">
                              {item.quantity}{" "}
                              {(() => {
                                const unitCode = item?.isSecondaryUnit
                                  ? item?.unitConfig?.secondaryUnitCode
                                  : item?.unitConfig?.primaryUnitCode;

                                // return unitCode !== "unit" ? unitCode : "";
                                return unitCode || "";
                              })()}
                            </td>
                            <td className="text-center">
                              ₹
                              {CurrencyConverter.toCurrency(
                                item.lineGrandTotal || 0,
                                "INR",
                              )}
                            </td>
                          </tr>
                        ))}
                        {/* <tr>
                          <td
                            colSpan={7}
                            className="force-style"
                            style={{
                              textAlign: "right",

                              fontWeight: 500,
                            }}
                          >
                            Grand Total
                          </td>
                          <td className="text-center">
                            ₹
                            {saleData
                              ? CurrencyConverter.toCurrency(
                                  saleData?.totalsoldprice || 0,
                                  "INR",
                                )
                              : "0.00"}
                          </td>
                        </tr> */}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Right Side - Return Cart */}
              <div className="col-lg-6">
                <div className="card mb-2">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Return Sale</h6>

                    {/* <button
                      className="btn btn-warning btn-sm"
                      onClick={() => setShowPreviousModal(true)}
                    >
                      <i className="bx bx-history me-1"></i>
                      Previous Returns
                    </button> */}
                  </div>

                  <div className="card-body px-0">
                    <div className="table-responsive mt-0">
                      <table className="table table-bordered align-middle mb-2">
                        <thead className="table-light">
                          <tr>
                            <th className="text-center">S.No</th>
                            <th>Items Name</th>
                            <th className="text-center">Rate</th>
                            <th className="text-center">Qty</th>
                            <th className="text-center">Total </th>
                            <th className="text-center">Action</th>
                          </tr>
                        </thead>

                        <tbody>
                          {returnCart.length === 0 ? (
                            <tr>
                              <td
                                colSpan="5"
                                className="text-center text-muted"
                              >
                                No products added
                              </td>
                            </tr>
                          ) : (
                            returnCart.map((item, index) => (
                              <tr
                                key={item.id}
                                className="position-relative  cursor-poInter hover-row"
                                onClick={() =>
                                  handleEditRetrunItem(item, index)
                                }
                              >
                                <td className="text-center">
                                  {index + 1}
                                  {item.imode === "SERVICE" && (
                                    <span className="rounded fsz-10 ms-1">
                                      <CircleIcon iClass={"text-yellow"}/>
                                    </span>
                                  )}
                                  {item.imode === "RECHARGE" && (
                                    <span className="rounded fsz-10 ms-1">
                                      <CircleIcon iClass={"text-brown"}/>
                                    </span>
                                  )}
                                  {item.imode === "PRODUCT" && (
                                    <span className="rounded fsz-10 ms-1">
                                      <CircleIcon iClass={"text-primary"}/>
                                    </span>
                                  )}
                                </td>
                                <td
                                  className="text-wrap"
                                  style={{ maxWidth: "250px" }}
                                >
                                  {item.title}
                                </td>
                                <td className="text-center">
                                  {CurrencyConverter.toCurrency(
                                    item.returnPrice || 0,
                                    "INR",
                                  )}
                                </td>

                                <td className="text-center">{item.quantity}</td>

                                <td className="text-center">
                                  ₹
                                  {CurrencyConverter.toCurrency(
                                    item.lineTotalReturnPrice || 0,
                                    "INR",
                                  )}
                                </td>

                                <td className="text-center">
                                  <div className="order-actions opacity-0 opacity-md-100">
                                    <button
                                      className="btn-label-primary shadow-none me-2"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditRetrunItem(item, index);
                                      }}
                                    >
                                      <EditIcon/>
                                    </button>
                                    <button
                                      className="btn-label-danger shadow-none"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteReturnItem(index);
                                      }}
                                    >
                                      <DeleteIcon/>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                    <div className="row bg-light border-0  text-center align-items-center m-0">
                      <div className="col p-2">
                        <p className="mb-0">Total Sold</p>
                        <h6 className="mb-0 fs-20">
                          ₹
                          {CurrencyConverter.toCurrency(
                            returnTotals?.totalSoldPrice,
                            "INR",
                          )}
                        </h6>
                      </div>
                      <div className="col p-2 fs-20">
                        <MinusIcon iClass={"text-muted"}/>
                      </div>
                      <div className="col p-2">
                        <p className="mb-0">Total Cancelled</p>
                        <h6 className="mb-0 fs-20">
                          ₹
                          {CurrencyConverter.toCurrency(
                            returnTotals?.totalCancelledPrice,
                            "INR",
                          )}
                        </h6>
                      </div>
                      <div className="col p-2 fs-20">
                        <LineDoubleIcon iClass={"text-muted"}/>
                      </div>
                      <div className="col bg-dark-shine p-1 radius-10">
                        <p className="mb-0 bg-dark-shine">Total Amount</p>
                        <h6 className="mb-0 fs-20">
                          ₹
                          {CurrencyConverter.toCurrency(
                            returnTotals?.totalReturnAmount,
                            "INR",
                          )}
                        </h6>
                      </div>
                      <div className="col p-2 fs-20">
                        <LineDoubleIcon iClass={"text-muted"}/>
                        <ChevronRightIcon iClass={"text-muted"}/>
                      </div>
                      <div className="col bg-dark-blue p-1">
                        <p className="mb-0 bg-dark-text fw-600 d-flex align-items-center justify-content-center gap-1">
                          Grand Total
                        </p>

                        <h6 className="mb-0 bg-dark-text fs-20 fw-600">
                          ₹
                          {CurrencyConverter.toCurrency(
                            returnTotals?.grandTotal,
                            "INR",
                          )}
                        </h6>
                      </div>
                    </div>

                    <div className="d-block ">
                      <div className="mt-2 ms-2 mb-0">
                        <button
                          type="button"
                          className="text-left btn-label-secondary rounded shadow-none border-0 me-2 px-2 py-1"
                          onClick={() => setRemarkModalOpen(true)}
                        >
                          <div className="d-flex flex-column align-items-start">
                            <span>
                              <EditIcon iClass={"me-2"}/>
                              Remark
                            </span>

                            {remark && (
                              <small
                                className="text-muted text-truncate d-block"
                                style={{ maxWidth: "120px" }}
                              >
                                {remark.split("\n")[0]}
                              </small>
                            )}
                          </div>
                        </button>
                      </div>
                      <button
                        className="btn btn-primary px-4 text-right"
                        onClick={() => setShowConfirmModal(true)}
                        disabled={proceedLoading || returnCart.length === 0}
                      >
                        {proceedLoading ? (
                          <>
                            <CircularProgress
                              size={18}
                              color="inherit"
                              className="me-2"
                            />
                          </>
                        ) : (
                          <>
                            <CheckIcon iClass={"me-1"}/>
                            Proceed
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* <PreviousReturnModal
              isOpen={showPreviousModal}
              onClose={() => setShowPreviousModal(false)}
              returnHistory={previousReturns}
            /> */}

            {isRetrunProductModalOpen && (
              <AddRetrunProduct
                data={isRetrunProductData}
                addToCart={handleAddToReturn}
                onClose={handleSoldPriceClose}
              />
            )}

            {editItemModalOpen && (
              <AddRetrunProduct
                data={editItem}
                addToCart={handleAddToReturn}
                onClose={handleCloseEditModal}
              />
            )}

            {!templateId && isViewTempModalOpen && (
              <InvoiceTempPreview
                isData={isViewData}
                onClose={() => setViewTempModalOpen(false)}
                onAdd={handleSetTemplate}
              />
            )}
         {templateId && (isViewModalOpen || isViewReturnModalOpen) && (
          <TemplateModalLayout
            isOpen={isViewModalOpen || isViewReturnModalOpen}
            isData={isViewReturnModalOpen? isViewReturnData: isViewData}
            selectedOption={isViewReturnModalOpen? selectedReturnOption: selectedOption}
            setSelectedOption={isViewReturnModalOpen? setSelectedReturnOption:setSelectedOption}
            onClose={isViewReturnModalOpen? handleRetrunClose: handleClose}
            setSaleData={setViewData}
            isActive={isActive} 
            
          />
           )}
            <PaymentMethod
              open={paymentModal}
              totalPayable={returnTotals?.grandTotal || 0}
              onClose={() => setPaymentModal(false)}
              onDone={handlePaymentDone}
            />

            <RemarkModal
              open={remarkModalOpen}
              initialValue={remark}
              onClose={() => setRemarkModalOpen(false)}
              onSave={(val) => setRemark(val)}
            />
          </div>
          {/* <SelectReturnProductModal
            isOpen={showProductModal}
            onClose={() => setShowProductModal(false)}
            products={selectedSaleProducts}
            onAddToCart={handleAddToReturn}
          /> */}

       {showConfirmModal && (
            <>
              <div className="modal-backdrop show"></div>

              <div
                className="modal show"
                id="CustomerModal"
                tabIndex="-1"
                aria-modal="true"
                role="dialog"
                style={{ display: "block" }}
              >
                <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">Return Preview</h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={handleCloseConfirmModal}
                        data-bs-dismiss="modal"
                        aria-label="Close"
                      ></button>
                    </div>
                    <div className="modal-body">
                        <div className="d-flex align-items-center mb-2 text-muted">
                          <InfoCircleIcon iClass={"me-2 text-primary fs-5"}/>
                          <span className="fw-medium">Please review the return details and summary before confirming:</span>
                        </div>
                        <div className="table-responsive bg-white rounded border mb-0 font-10">
                          <table className="table  align-middle mb-0">
                              <colgroup>
                              <col style={{ width: "50px" }} />   {/* S.No */}
                              <col />                             {/* Product - auto */}
                              <col style={{ width: "90px" }} />   {/* Rate */}
                              <col style={{ width: "70px" }} />   {/* Qty */}
                              <col style={{ width: "90px" }} />  {/* Total */}
                            </colgroup>
                            <thead className="table-light">
                              <tr>
                                <th>S.No</th>
                                <th>Product</th>                            
                                <th className="text-end">Rate</th>
                                <th className="text-center">Qty</th>
                                <th className="text-end">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {returnCart.map((item, index) => (
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td>{item.title}</td>
                                  
                                  <td className="text-end">
                                    ₹{CurrencyConverter.toCurrency(item.returnPrice, "INR")}
                                  </td>
                                  <td className="text-center">{item.quantity}</td>
                                  <td className="text-end">
                                    ₹{CurrencyConverter.toCurrency(item.lineTotalReturnPrice, "INR")}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>                       
                        <div className="px-3 py-2 mt-0">
                          <div className="row bg-light border rounded text-center align-items-center font-10">
                          <div className="col p-2 ">
                            <p className="mb-0">Total Sold</p>
                            <h6 className="mb-0 font-12">
                              ₹
                              {CurrencyConverter.toCurrency(
                                returnTotals?.totalSoldPrice,
                                "INR",
                              )}
                            </h6>
                          </div>
                          <div className="col p-2 ">
                            <MinusIcon iClass={"text-muted"}/>
                          </div>
                          <div className="col p-2 ">
                            <p className="mb-0">Total Cancelled</p>
                            <h6 className="mb-0 font-12 text-danger">
                            - ₹
                              {CurrencyConverter.toCurrency(
                                returnTotals?.totalCancelledPrice,
                                "INR",
                              )}
                            </h6>
                          </div>
                          <div className="col p-2 ">
                            <LineDoubleIcon iClass={"text-muted"}/>
                          </div>
                          <div className="col ">
                            <p className="mb-0 ">Return Amount</p>
                            <h6 className="mb-0 font-10">
                              ₹
                              {CurrencyConverter.toCurrency(
                                returnTotals?.totalReturnAmount,
                                "INR",
                              )}
                            </h6>
                          </div>
                          <div className="col p-2 ">
                          <LineDoubleIcon iClass={"text-muted"}/>
                            <ChevronRightIcon iClass={"text-muted"}/>
                          </div>
                          <div className="col  p-1">
                            <p className="mb-0 bg-dark-text fw-600 d-flex align-items-center justify-content-center gap-1">
                              Grand Total
                            </p>

                            <h6 className="mb-0 text-primary font-10 fw-bold">
                              ₹
                              {CurrencyConverter.toCurrency(
                                returnTotals?.grandTotal,
                                "INR",
                              )}
                            </h6>
                          </div>
                        </div>
                        </div>                      
                    </div>                  

                    <div className="modal-footer d-block">
                      <button
                        className="btn btn-outline-secondary text-left shadow-none "
                        onClick={() => setShowConfirmModal(false)}
                      >
                        <XIcon/> No
                      </button>

                      <button
                        className="btn btn-primary text-right shadow-none"
                        onClick={() => {
                          setShowConfirmModal(false);
                          setPaymentModal(true);
                        }}
                      >
                        <CheckIcon/> Confirm & Proceed
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        
        </div>
      </div>
    </>
  );
};
export default ReturnSale;


